import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { AtticScope, CellarScope, MeasurementOnSiteAdditionalInformationType, MeasurementOnSitePropertyType, MeasurementOnSiteRequestDTO, isAtticScopeMeasured, isCellarScopeMeasured } from 'models/purchaseFlow/measurementDTO'
import { useCallback, useEffect, useRef, useState } from 'react'

import { FeeDTO } from 'models/fee'
import { Nullable } from 'models/helpers'
import { ProductKind } from 'constants/product'
import { bigFromFee } from 'utils/price/decimals'
import constate from 'constate'
import { logger } from 'utils/logging'
import { useMeasurementRateCard } from './useMeasurementRateCard'
import { usePurchaseFlowProducts } from '../../_main/contexts'

/** Redesigned flow (ISSUE-2187): minimum residential units per unit-based property type (Two-family house / Multi-unit building) */
export const RedesignMinimumUnitsForPropertyType = new Map<MeasurementOnSitePropertyType, number>([
  [MeasurementOnSitePropertyType.SEMI_DETACHED_HOUSE, 2],
  [MeasurementOnSitePropertyType.APARTMENT_BUILDING, 3],
])

/**
 * Redesigned flow: floors a non-unit property type starts with. House (Kat. 2) starts at 2 because its price
 * list is written per floor with the first two at the base rate ("2 floors included"), and Mila's prototype
 * preselects 2 — landing on 1 floor instead quoted the Kat. 1 fallback price and read as a bug (ISSUE-2861).
 * QA round 1 briefly started the House at 1 floor to match the legacy flow; QA round 2 restored 2 and instead
 * collapses a bare single-floor House to Kat. 1 (MeasurementStepRedesign.controller), so category and quoted
 * rate can never diverge. A single-storey house with a cellar or attic still lowers the counter to 1.
 */
export const RedesignDefaultFloorsForPropertyType = new Map<MeasurementOnSitePropertyType, number>([
  [MeasurementOnSitePropertyType.SINGLE_FAMILY_HOUSE, 2],
])

/** Floors a non-unit property type starts with — 1 unless the category carries its own default */
export const redesignDefaultFloors = (propertyType: MeasurementOnSitePropertyType): number =>
  RedesignDefaultFloorsForPropertyType.get(propertyType) ?? 1

/** The property type preselected when the step opens (ISSUE-2187) */
const DEFAULT_PROPERTY_TYPE = MeasurementOnSitePropertyType.APARTMENT_SINGLE_FLOORS

/** Whether the rate card prices an add-on (ISSUE-2838). Non-positive counts as not offered — an unconfigured SF row syncs as a zero fee, and a "+0.00 €" card is a support ticket. */
const isAddOnRatePriced = (rate?: Nullable<FeeDTO>): boolean => !!rate && bigFromFee(rate).gt(0)

export const [MeasurementsContextProvider, useMeasurements] = constate(() => {
  const { selectedProducts } = usePurchaseFlowProducts()

  const [measurementPropertyType, setMeasurementPropertyType] = useState<MeasurementOnSitePropertyType>(DEFAULT_PROPERTY_TYPE)
  const [surfaceArea, setSurfaceArea] = useState<number>(0)
  const [numberOfFloors, setNumberOfFloors] = useState<number>(0)
  const [measurementComments, setMeasurementComments] = useState<string>('')
  const [additionalInformation, setAdditionalInformation] = useState<Set<MeasurementOnSiteAdditionalInformationType>>(new Set())

  // Floors per residential unit for the unit-based property types (ISSUE-2187)
  const [residentialUnits, setResidentialUnits] = useState<number[]>([])
  // Cellar/attic scope + presence model (ISSUE-2459)
  const [cellarScope, setCellarScope] = useState<CellarScope>(CellarScope.NONE)
  const [atticScope, setAtticScope] = useState<AtticScope>(AtticScope.NONE)
  // Add-on selections (ISSUE-2838) — priced off the quote, never attached to the cart as products
  const [isExpressServiceSelected, setIsExpressServiceSelected] = useState<boolean>(false)
  const [isAdditionalHouseViewsSelected, setIsAdditionalHouseViewsSelected] = useState<boolean>(false)

  // Availability lives here, next to the selections and both payload builders, so the payload gate can never
  // be weaker than the render gate. Same react-query key as the step's hint card, so this adds no request.
  const pricing = useMeasurementRateCard().data?.data

  /** False hides the Express card AND drops its selection */
  const isExpressServiceOffered = isAddOnRatePriced(pricing?.expressService)

  /** Offered for all 4 property categories (decided 2026-08-21) — only the rate card gates it */
  const isAdditionalHouseViewsOffered = isAddOnRatePriced(pricing?.additionalHouseViews)

  /** A priced add-on is required: against a BE that predates the add-ons, attaching both booleans to EVERY quote and placement would 400 the whole flow on a strict deserializer. */
  const isMeasurementAddOnsEnabled = isExpressServiceOffered || isAddOnRatePriced(pricing?.additionalHouseViews)

  /** Whether the selected property type is configured per residential unit */
  const isUnitBasedPropertyType = RedesignMinimumUnitsForPropertyType.has(measurementPropertyType)

  // Reset all measurement form state whenever there is no MEASUREMENT_ON_SITE product selected
  // Otherwise the checkbox / counter values stay filled in while the corresponding cart options have been cleared,
  // producing a desync where the summary price ignores Basement/Attic/etc.
  const hasMeasurementProduct = Object.values(selectedProducts).some(product => product.kind === ProductKind.MEASUREMENT_ON_SITE)
  useEffect(() => {
    if (hasMeasurementProduct) return

    setMeasurementPropertyType(DEFAULT_PROPERTY_TYPE)
    setSurfaceArea(0)
    setNumberOfFloors(0)
    setMeasurementComments('')
    setAdditionalInformation(new Set())
    setResidentialUnits([])
    setCellarScope(CellarScope.NONE)
    setAtticScope(AtticScope.NONE)
    setIsExpressServiceSelected(false)
    setIsAdditionalHouseViewsSelected(false)
  }, [hasMeasurementProduct])

  // An add-on that stops being offered hides its card — drop the selection too, or an invisible checkbox keeps billing
  useEffect(() => {
    if (!isExpressServiceOffered) setIsExpressServiceSelected(false)
  }, [isExpressServiceOffered])

  useEffect(() => {
    if (!isAdditionalHouseViewsOffered) setIsAdditionalHouseViewsSelected(false)
  }, [isAdditionalHouseViewsOffered])

  // Losing a ticked add-on because its rate went away is the anomalous case worth a signal — the client silently
  // stops being billed for something they picked. Product resets drop selections by design and stay quiet.
  const wasExpressServiceSelected = useRef(false)
  const wasAdditionalHouseViewsSelected = useRef(false)
  useEffect(() => {
    wasExpressServiceSelected.current = isExpressServiceSelected
    wasAdditionalHouseViewsSelected.current = isAdditionalHouseViewsSelected
  }, [isExpressServiceSelected, isAdditionalHouseViewsSelected])

  useEffect(() => {
    const revokedExpress = wasExpressServiceSelected.current && !isExpressServiceOffered
    const revokedHouseViews = wasAdditionalHouseViewsSelected.current && !isAddOnRatePriced(pricing?.additionalHouseViews)
    if (!revokedExpress && !revokedHouseViews) return

    logger.warn('Measurement add-on selection revoked — the rate card no longer prices it', undefined, {
      context: {
        component: 'MeasurementsContext',
        metadata: { revokedExpress, revokedHouseViews, hasRateCard: !!pricing }
      }
    })
  }, [isExpressServiceOffered, pricing])

  useEffect(() => {
    const minUnits = RedesignMinimumUnitsForPropertyType.get(measurementPropertyType)
    if (minUnits) setResidentialUnits(units => units.length < minUnits ? Array.from({ length: minUnits }, () => 1) : units)
    if (!minUnits && numberOfFloors === 0) setNumberOfFloors(redesignDefaultFloors(measurementPropertyType))
  }, [measurementPropertyType, residentialUnits, numberOfFloors])

  const getMeasurementRequestData = useCallback((): MeasurementOnSiteRequestDTO => {
    return {
      propertyType: measurementPropertyType,
      surfaceArea: Number(surfaceArea),
      // Rooms are omitted entirely (ISSUE-2188 cutover)
      // Unit-based property types carry floors per unit — the payload keeps the flat totals
      numberOfFloors: isUnitBasedPropertyType ? residentialUnits.reduce((sum, unitFloors) => sum + unitFloors, 0) : numberOfFloors,
      // There is no unit input for non-unit types (Kat 1/2) — omit the field instead of sending 0
      ...(isUnitBasedPropertyType ? { numberOfResidentialUnits: residentialUnits.length } : {}),
      // The scope/presence model (ISSUE-2459) keeps a room-only cellar from being charged at placement;
      // the deprecated booleans stay consistent with the scopes (= scope measured-ness) per the transitional BE contract
      basementAreaToMeasure: isCellarScopeMeasured(cellarScope),
      atticAreaToMeasure: isAtticScopeMeasured(atticScope),
      cellarScope,
      atticScope,
      // Add-ons (ISSUE-2838) — this one builder feeds both the quote and the order, so the panel total and the billed total cannot diverge
      ...(isMeasurementAddOnsEnabled
        ? {
          expressService: isExpressServiceOffered && isExpressServiceSelected,
          additionalHouseViews: isAdditionalHouseViewsOffered && isAdditionalHouseViewsSelected
        }
        : {}),
      additionalInformation: Array.from(additionalInformation),
      comment: measurementComments
    }
  }, [additionalInformation, atticScope, cellarScope, isAdditionalHouseViewsOffered, isAdditionalHouseViewsSelected, isExpressServiceOffered, isMeasurementAddOnsEnabled, isExpressServiceSelected, isUnitBasedPropertyType, measurementComments, measurementPropertyType, numberOfFloors, residentialUnits, surfaceArea])

  const logMeasurementRequirements = useCallback(() => {
    const additionalAreas = []
    if (isCellarScopeMeasured(cellarScope)) additionalAreas.push(ProductKind.EXTRA_MEASURED_BASEMENT)
    if (isAtticScopeMeasured(atticScope)) additionalAreas.push(ProductKind.EXTRA_MEASURED_ATTIC)

    logAnalyticsEvent(AnalyticsEvent.MEASUREMENTS_REQUIREMENT_SET_UP, {
      PropertyType: measurementPropertyType,
      SurfaceArea: surfaceArea,
      NumberRooms: 0,
      NumberResidentialUnit: isUnitBasedPropertyType ? residentialUnits.length : 0,
      AdditionalAreas: additionalAreas,
      // Raw scope model (ISSUE-2459): keeps PRESENT_NOT_MEASURED distinguishable from NONE in analytics
      CellarScope: cellarScope,
      AtticScope: atticScope,
      // Add-on selections (ISSUE-2838)
      ...(isMeasurementAddOnsEnabled
        ? {
          ExpressService: isExpressServiceOffered && isExpressServiceSelected,
          AdditionalHouseViews: isAdditionalHouseViewsOffered && isAdditionalHouseViewsSelected,
          // Attach-rate denominator: zero uptake vs. cards that never rendered
          ExpressServiceOffered: isExpressServiceOffered,
          AdditionalHouseViewsOffered: isAdditionalHouseViewsOffered
        }
        : {}),
      MeasurementReason: Array.from(additionalInformation),
      Comments: measurementComments,
      NumberFloors: isUnitBasedPropertyType ? residentialUnits.reduce((sum, unitFloors) => sum + unitFloors, 0) : numberOfFloors,
    })
  }, [additionalInformation, atticScope, cellarScope, isAdditionalHouseViewsOffered, isAdditionalHouseViewsSelected, isExpressServiceOffered, isMeasurementAddOnsEnabled, isExpressServiceSelected, isUnitBasedPropertyType, measurementComments, measurementPropertyType, numberOfFloors, residentialUnits, surfaceArea])

  return {
    isUnitBasedPropertyType,
    isMeasurementAddOnsEnabled,
    isExpressServiceOffered,
    isAdditionalHouseViewsOffered,
    isExpressServiceSelected,
    isAdditionalHouseViewsSelected,
    surfaceArea,
    numberOfFloors,
    cellarScope,
    atticScope,
    residentialUnits,
    measurementComments,
    additionalInformation,
    measurementPropertyType,
    setSurfaceArea,
    setNumberOfFloors,
    setCellarScope,
    setAtticScope,
    setIsExpressServiceSelected,
    setIsAdditionalHouseViewsSelected,
    setResidentialUnits,
    setMeasurementComments,
    setMeasurementPropertyType,
    getMeasurementRequestData,
    setAdditionalInformation,
    logMeasurementRequirements
  }
})
